import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { AddKeyAccount, UpdateEstimates } from '@repo/backend/crt'
import { ProductCategory } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export type KeyAccountContractEstimatesSearch = {
  productCategory: ProductCategory
  crtPeriod?: string
  manualInvestmentIds?: number[]
}

export type KeyAccountContractEstimatesList = Awaited<
  ReturnType<typeof getKeyAccountContractEstimates>
>
export type KeyAccountContractEstimateRow = KeyAccountContractEstimatesList['results'][number]
export type PreviousPeriodCopyCounts = Awaited<ReturnType<typeof getPreviousPeriodCopyCounts>>

async function getKeyAccountContractEstimates(
  search: KeyAccountContractEstimatesSearch,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates'].$get(
    {
      query: {
        productCategory: search.productCategory,
        ...(search.crtPeriod ? { crtPeriod: search.crtPeriod } : {}),
        ...(search.manualInvestmentIds?.length
          ? { manualInvestmentIds: JSON.stringify(search.manualInvestmentIds) }
          : {}),
      },
    },
    { init: { signal } },
  )
  return await res.json()
}

async function getPreviousPeriodCopyCounts(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates']['copy-previous'].$get(
    { query: stringifyValues({ crtPeriod }) },
    { init: { signal } },
  )
  return await res.json()
}

async function updateKeyAccountContractEstimates(data: UpdateEstimates) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates'].$patch({ json: data })
  return await res.json()
}

async function addKeyAccountContractEstimates(data: AddKeyAccount) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates'].$post({ json: data })
  return await res.json()
}

async function removeKeyAccountContractEstimates(hierarchyErp: string, crtPeriod?: string) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates'][':hierarchyErp'].$delete({
    param: { hierarchyErp },
    query: stringifyValues({ crtPeriod }),
  })
  return await res.json()
}

async function copyPreviousKeyAccountContractEstimates(crtPeriod?: string) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates']['copy-previous'].$post({
    json: { crtPeriod },
  })
  return await res.json()
}

async function getAvailableHierarchiesForEstimates(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates']['available-hierarchies'].$get(
    { query: stringifyValues({ crtPeriod }) },
    { init: { signal } },
  )
  return await res.json()
}

export type AvailableHierarchyForEstimates = Awaited<
  ReturnType<typeof getAvailableHierarchiesForEstimates>
>[number]

export function useAvailableHierarchiesForEstimates(crtPeriod?: string) {
  return useQuery({
    queryKey: ['availableHierarchiesForEstimates', crtPeriod],
    queryFn: ({ signal }) => getAvailableHierarchiesForEstimates(crtPeriod, signal),
  })
}

// The export's query string: the period, plus the manual investments that count toward the accruals.
function estimatesExportQuery(crtPeriod: string, manualInvestmentIds?: number[]) {
  const params = new URLSearchParams({ crtPeriod })
  if (manualInvestmentIds?.length) {
    params.set('manualInvestmentIds', JSON.stringify(manualInvestmentIds))
  }
  return params.toString()
}

export function keyAccountContractEstimatesExportQueryOptions(
  crtPeriod: string,
  manualInvestmentIds?: number[],
) {
  return {
    queryKey: [
      'keyAccountContractEstimatesExport',
      crtPeriod,
      manualInvestmentIds?.join(',') ?? null,
    ] as const,
    queryFn: () =>
      fetchCsvExport(
        `/crt/key-accounts/contract-estimates/export?${estimatesExportQuery(crtPeriod, manualInvestmentIds)}`,
      ),
  }
}

export function keyAccountContractEstimatesWithRatiosExportQueryOptions(
  crtPeriod: string,
  manualInvestmentIds?: number[],
) {
  return {
    // Shares the `keyAccountContractEstimatesExport` prefix so every cache drop below (and the one in
    // `useUpdateKeyAccountContractRatios`) clears both exports at once.
    queryKey: [
      'keyAccountContractEstimatesExport',
      crtPeriod,
      manualInvestmentIds?.join(',') ?? null,
      'withPerformanceRatios',
    ] as const,
    queryFn: () =>
      fetchCsvExport(
        `/crt/key-accounts/contract-estimates/export/with-performance-ratios?${estimatesExportQuery(crtPeriod, manualInvestmentIds)}`,
      ),
  }
}

export function useKeyAccountContractEstimates(search: KeyAccountContractEstimatesSearch) {
  return useQuery({
    queryKey: ['keyAccountContractEstimates', search],
    queryFn: ({ signal }) => getKeyAccountContractEstimates(search, signal),
    refetchOnWindowFocus: false,
  })
}

async function getKeyAccountContractEstimatesByHierarchy(
  hierarchyErp: string,
  crtPeriod: string | undefined,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['key-accounts']['contract-estimates'][':hierarchyErp'].$get(
    { param: { hierarchyErp }, query: stringifyValues({ crtPeriod }) },
    { init: { signal } },
  )
  return await res.json()
}

export type KeyAccountContractEstimatesByHierarchy = Awaited<
  ReturnType<typeof getKeyAccountContractEstimatesByHierarchy>
>

export function useKeyAccountContractEstimatesByHierarchy(
  hierarchyErp: string | undefined,
  crtPeriod: string | undefined,
) {
  return useQuery({
    queryKey: ['keyAccountContractEstimatesByHierarchy', hierarchyErp, crtPeriod],
    queryFn: ({ signal }) =>
      getKeyAccountContractEstimatesByHierarchy(hierarchyErp as string, crtPeriod, signal),
    enabled: !!hierarchyErp,
    refetchOnWindowFocus: false,
  })
}

export function usePreviousPeriodCopyCounts(crtPeriod?: string) {
  return useQuery({
    queryKey: ['previousPeriodCopyCounts', crtPeriod],
    queryFn: ({ signal }) => getPreviousPeriodCopyCounts(crtPeriod, signal),
  })
}

/**
 * Invalidates every query whose data depends on the key-account definition, estimates, or accrual
 * totals: the estimates grid, the budget tracker (incl. its warning counts), available hierarchies,
 * and the copy-previous counts. Any mutation that changes estimates, master-agreement → contract
 * clause mappings, or manual investments must call this so the key-account pages stay in sync.
 */
export function invalidateKeyAccountQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['keyAccountContractEstimates'] })
  queryClient.invalidateQueries({ queryKey: ['keyAccountContractEstimatesByHierarchy'] })
  queryClient.invalidateQueries({ queryKey: ['previousPeriodCopyCounts'] })
  queryClient.invalidateQueries({ queryKey: ['availableHierarchiesForEstimates'] })
  queryClient.invalidateQueries({ queryKey: ['budgetTrackerByKeyAccount'] })

  // Ensure that exports never hit cached data after a mapping change.
  queryClient.removeQueries({ queryKey: ['keyAccountContractEstimatesExport'] })
  queryClient.removeQueries({ queryKey: ['budgetTrackerByKeyAccountExport'] })
}

export function useUpdateKeyAccountContractEstimates({ handleOnSuccess }: QueryHandlers = {}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateKeyAccountContractEstimates'],
    mutationFn: (data: UpdateEstimates) => updateKeyAccountContractEstimates(data),
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.keyAccountContractEstimates.updateFailed',
        'crt',
      )
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
    // Keep the mutation "pending" (its loading state) until the *visible grid* refetch settles.
    // The estimates grid holds the just-typed values in `localEdits` throughout, so the cell never
    // flashes back to the stale server value; `handleOnSuccess` only clears those edits once fresh
    // grid data has arrived. Only the grid is on screen during an in-grid save, so we gate the
    // loading state on its refetch alone. The other key-account queries (edit-dialog seed,
    // add-dialog available hierarchies, copy counts, budget tracker) aren't visible here — they are
    // invalidated in the background and reconcile on their next mount, so a closed dialog's slow
    // query can't prolong the save.
    onSettled: async (_data, error) => {
      await queryClient.invalidateQueries({ queryKey: ['keyAccountContractEstimates'] })
      void queryClient.invalidateQueries({ queryKey: ['previousPeriodCopyCounts'] })
      void queryClient.invalidateQueries({ queryKey: ['availableHierarchiesForEstimates'] })
      // Drop (not just invalidate) the tracker table and the edit-dialog seed. Both are off-screen
      // during an in-grid save, so invalidation would leave their stale cache in place: on return
      // the tracker (gated on `isPending`) would flash the old totals then reload, and the edit
      // dialog would seed the old per-hierarchy values. Removing the cache forces a cold fetch on
      // next mount — the tracker shows its loading state and the dialog waits before seeding, so
      // both render fresh values with no old-then-swap flash.
      queryClient.removeQueries({ queryKey: ['keyAccountContractEstimatesByHierarchy'] })
      queryClient.removeQueries({ queryKey: ['budgetTrackerByKeyAccount'] })

      // Ensure that exports never hit cached data after a mapping change.
      queryClient.removeQueries({ queryKey: ['keyAccountContractEstimatesExport'] })
      queryClient.removeQueries({ queryKey: ['budgetTrackerByKeyAccountExport'] })
      if (error) return
      handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('keyAccountContractEstimates.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
  })
}

export function useAddKeyAccountToEstimates({ handleOnSuccess }: QueryHandlers = {}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['addKeyAccountToEstimates'],
    mutationFn: (data: AddKeyAccount) => addKeyAccountContractEstimates(data),
    onSuccess: () => {
      handleOnSuccess?.()
      invalidateKeyAccountQueries(queryClient)
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('keyAccountContractEstimates.addSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('keyAccountContractEstimates.addFailed', { ns: 'crt' }),
        className: toastClassName,
      })
    },
  })
}

export function useRemoveKeyAccountFromEstimates({ handleOnSuccess }: QueryHandlers = {}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['removeKeyAccountFromEstimates'],
    mutationFn: ({ hierarchyErp, crtPeriod }: { hierarchyErp: string; crtPeriod?: string }) =>
      removeKeyAccountContractEstimates(hierarchyErp, crtPeriod),
    onSuccess: () => {
      handleOnSuccess?.()
      invalidateKeyAccountQueries(queryClient)
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('keyAccountContractEstimates.removeSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('keyAccountContractEstimates.removeFailed', { ns: 'crt' }),
        className: toastClassName,
      })
    },
  })
}

export function useCopyPreviousKeyAccountContractEstimates({
  handleOnSuccess,
}: QueryHandlers = {}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['copyPreviousKeyAccountContractEstimates'],
    mutationFn: (crtPeriod?: string) => copyPreviousKeyAccountContractEstimates(crtPeriod),
    onSuccess: (data) => {
      handleOnSuccess?.()
      invalidateKeyAccountQueries(queryClient)
      toast.success(t('toast.success', { ns: 'common' }), {
        description: [
          t('keyAccountContractEstimates.copySuccess', {
            ns: 'crt',
            count: data.copiedErpCount,
          }),
          t('keyAccountContractEstimates.copySuccessRows', {
            ns: 'crt',
            count: data.copiedEstimateCount,
          }),
          data.copiedRatioCount > 0
            ? t('keyAccountContractEstimates.copySuccessRatios', {
                ns: 'crt',
                count: data.copiedRatioCount,
              })
            : null,
        ]
          .filter(Boolean)
          .join(' '),
        className: toastClassName,
      })
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('keyAccountContractEstimates.copyFailed', { ns: 'crt' }),
        className: toastClassName,
      })
    },
  })
}
